import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  CreateValuationInput,
  UpdateValuationInput,
  Valuation,
  ValuationSource,
} from "../../lib/realestate";
import { Modal } from "../ui/Modal";

const SOURCES: ValuationSource[] = [
  "KAUFPREIS",
  "GUTACHTEN",
  "EIGENE_SCHAETZUNG",
  "VERKEHRSWERT_BERICHT",
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: CreateValuationInput | UpdateValuationInput) => Promise<void>;
  existing: Valuation | null;
  submitting: boolean;
}

function parseEuroDe(input: string): number {
  const trimmed = input.replace(/\./g, "").replace(",", ".").trim();
  const n = Number.parseFloat(trimmed);
  if (Number.isNaN(n) || n <= 0) throw new Error("invalid_amount");
  return Math.round(n * 100);
}

function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}

function centsToEuroDe(cents: number): string {
  return (cents / 100).toFixed(2).replace(".", ",");
}

export function ValuationFormModal({ open, onClose, onSubmit, existing, submitting }: Props) {
  const { t } = useTranslation();
  const [stichtag, setStichtag] = useState(existing?.stichtag ?? todayIso());
  const [value, setValue] = useState(existing ? centsToEuroDe(existing.valueCents) : "");
  const [source, setSource] = useState<ValuationSource>(existing?.source ?? "EIGENE_SCHAETZUNG");
  const [notes, setNotes] = useState(existing?.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStichtag(existing?.stichtag ?? todayIso());
    setValue(existing ? centsToEuroDe(existing.valueCents) : "");
    setSource(existing?.source ?? "EIGENE_SCHAETZUNG");
    setNotes(existing?.notes ?? "");
    setError(null);
  }, [existing]);

  const title = existing
    ? t("realestate.valuation.form_title_edit")
    : t("realestate.valuation.add_title");

  async function handleSubmit() {
    setError(null);
    try {
      const valueCents = parseEuroDe(value);
      const trimmedNotes = notes.trim();
      await onSubmit({
        stichtag,
        valueCents,
        source,
        notes: trimmedNotes === "" ? null : trimmedNotes,
      });
      if (!existing) {
        setValue("");
        setNotes("");
      }
      onClose();
    } catch {
      setError(t("realestate.errors.invalid_amount"));
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-sm">{t("realestate.valuation.stichtag")}</span>
          <input
            type="date"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={stichtag}
            max={todayIso()}
            onChange={(e) => setStichtag(e.target.value)}
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.valuation.value")}</span>
          <input
            inputMode="decimal"
            className="block mt-1 w-full border rounded px-2 py-1"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="250.000,00"
          />
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.valuation.source")}</span>
          <select
            className="block mt-1 w-full border rounded px-2 py-1"
            value={source}
            onChange={(e) => setSource(e.target.value as ValuationSource)}
          >
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {t(`realestate.valuation_source.${s}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-sm">{t("realestate.valuation.notes")}</span>
          <textarea
            className="block mt-1 w-full border rounded px-2 py-1"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={500}
            rows={2}
          />
        </label>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-1 border rounded">
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-3 py-1 bg-blue-600 text-white rounded"
          >
            {submitting ? t("common.submitting") : t("common.save")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
